/**
 * Support Page Configuration
 *
 * "Support Page&Scope" dokümanından türetilen kategori/madde/field matrisi.
 * Kategori, madde, model, source ve locale bilgileri backend'in
 * yapılandırılmış alanlarıyla gönderilir; message alanı yalnızca
 * kullanıcının yazdığı metni içerir.
 */

import type { LucideIcon } from "lucide-react";
import {
  Bug,
  Coins,
  CreditCard,
  MessageCircleQuestion,
  ShieldCheck,
  Shirt,
  Sparkles,
  UserRound,
} from "lucide-react";

import type { CategoryColor } from "./category-colors";

export type FieldRequirement = "required" | "optional";

/** Backend'in category enum'u */
export type BackendCategory =
  | "general"
  | "billing"
  | "technical"
  | "account"
  | "feature_request";

export interface SupportIssue {
  id: string;
  /** i18n key — UI'da gösterilen çeviri */
  labelKey: string;
  /** Mail metadata bloğunda kullanılan sabit İngilizce etiket */
  emailLabel: string;
  /** Field 3 — "Choose the model you are having issue" dropdown'ı */
  model?: FieldRequirement;
  /** Field 4 — mesaj kutusu (default: optional) */
  message?: FieldRequirement;
  /** Field 5 — görsel ekleri (default: true) */
  attachments?: boolean;
  /** Madde seçilince gösterilen bilgilendirme banner'ı (i18n key) */
  infoBannerKey?: string;
}

export interface SupportCategory {
  id: string;
  labelKey: string;
  /** Kategori kartında gösterilen kısa açıklama (i18n key) */
  descriptionKey: string;
  emailLabel: string;
  /** Backend'e gönderilecek enum değeri */
  backendCategory: BackendCategory;
  /** Kategori kartı ikonu */
  icon: LucideIcon;
  /** Kategori kartı ikon rengi (kartları görsel olarak ayırt etmek için) */
  color: CategoryColor;
  issues: SupportIssue[];
}

export const MAX_ATTACHMENTS = 3;

export const supportCategories: SupportCategory[] = [
  {
    id: "account",
    labelKey: "support.categories.account",
    descriptionKey: "support.categoryDescriptions.account",
    emailLabel: "Account Related",
    backendCategory: "account",
    icon: UserRound,
    color: "indigo",
    issues: [
      {
        id: "login-problem",
        labelKey: "support.issues.loginProblem",
        emailLabel: "Login problem",
      },
      {
        id: "password-reset-mail",
        labelKey: "support.issues.passwordResetMail",
        emailLabel: "Password reset mail did not receive",
      },
      {
        id: "email-verification-mail",
        labelKey: "support.issues.emailVerificationMail",
        emailLabel: "Email verification mail did not receive",
      },
      {
        id: "account-recovery",
        labelKey: "support.issues.accountRecovery",
        emailLabel: "Account recovery problems",
      },
      {
        id: "multi-device-login",
        labelKey: "support.issues.multiDeviceLogin",
        emailLabel: "Multi-device login issues",
      },
    ],
  },
  {
    id: "ai-tools",
    labelKey: "support.categories.aiTools",
    descriptionKey: "support.categoryDescriptions.aiTools",
    emailLabel: "AI Tools / LLM Usage Issues",
    backendCategory: "technical",
    icon: Sparkles,
    color: "violet",
    issues: [
      {
        id: "llm-not-responding",
        labelKey: "support.issues.llmNotResponding",
        emailLabel: "LLM is not responding",
        model: "required",
      },
      {
        id: "llm-selection-error",
        labelKey: "support.issues.llmSelectionError",
        emailLabel: "LLM selection error",
        model: "required",
      },
      {
        id: "llm-response-mismatch",
        labelKey: "support.issues.llmResponseMismatch",
        emailLabel: "LLM response does not match request",
        model: "required",
      },
      {
        id: "low-quality-output",
        labelKey: "support.issues.lowQualityOutput",
        emailLabel: "Wrong / low-quality output",
        model: "required",
        infoBannerKey: "support.banners.outputQuality",
      },
      {
        id: "file-upload-issue",
        labelKey: "support.issues.fileUploadIssue",
        emailLabel: "File upload issue (image/audio/video)",
        model: "required",
      },
      {
        id: "broken-output",
        labelKey: "support.issues.brokenOutput",
        emailLabel: "No output or broken output",
        model: "required",
      },
      {
        id: "model-switching",
        labelKey: "support.issues.modelSwitching",
        emailLabel: "Model switching issues",
        model: "required",
      },
    ],
  },
  {
    id: "credits-billing",
    labelKey: "support.categories.creditsBilling",
    descriptionKey: "support.categoryDescriptions.creditsBilling",
    emailLabel: "Credits & Billing",
    backendCategory: "billing",
    icon: Coins,
    color: "teal",
    issues: [
      {
        id: "credits-not-delivered",
        labelKey: "support.issues.creditsNotDelivered",
        emailLabel: "Credits not delivered",
      },
      {
        id: "wrong-credit-deduction",
        labelKey: "support.issues.wrongCreditDeduction",
        emailLabel: "Wrong credit deduction",
        model: "optional",
      },
      {
        id: "credit-deducted-no-response",
        labelKey: "support.issues.creditDeductedNoResponse",
        emailLabel: "Credit deducted but model not responding",
        model: "optional",
      },
      {
        id: "credit-balance",
        labelKey: "support.issues.creditBalance",
        emailLabel: "Credit balance issues",
      },
      {
        id: "package-benefits",
        labelKey: "support.issues.packageBenefits",
        emailLabel: "Package benefits have not been assigned",
      },
    ],
  },
  {
    id: "payments",
    labelKey: "support.categories.payments",
    descriptionKey: "support.categoryDescriptions.payments",
    emailLabel: "Payments & Refunds",
    backendCategory: "billing",
    icon: CreditCard,
    color: "sky",
    issues: [
      {
        id: "payment-failed",
        labelKey: "support.issues.paymentFailed",
        emailLabel: "Payment failed",
      },
      {
        id: "payment-method-error",
        labelKey: "support.issues.paymentMethodError",
        emailLabel: "Payment method addition error",
      },
      {
        id: "subscription-issues",
        labelKey: "support.issues.subscriptionIssues",
        emailLabel: "Subscription issues",
      },
      {
        id: "refund-request",
        labelKey: "support.issues.refundRequest",
        emailLabel: "Refund requests",
      },
      {
        id: "invoice-request",
        labelKey: "support.issues.invoiceRequest",
        emailLabel: "Invoice requests",
      },
    ],
  },
  {
    id: "bug-report",
    labelKey: "support.categories.bugReport",
    descriptionKey: "support.categoryDescriptions.bugReport",
    emailLabel: "Bug Report",
    backendCategory: "technical",
    icon: Bug,
    color: "rose",
    issues: [
      {
        id: "model-not-loading",
        labelKey: "support.issues.modelNotLoading",
        emailLabel: "LLM / AI model not loading",
        model: "optional",
      },
      {
        id: "website-usability",
        labelKey: "support.issues.websiteUsability",
        emailLabel: "Issues related to website usability",
      },
      {
        id: "website-slow",
        labelKey: "support.issues.websiteSlow",
        emailLabel: "Website performance slow",
      },
      {
        id: "website-crashing",
        labelKey: "support.issues.websiteCrashing",
        emailLabel: "Website constantly crashing",
        message: "required",
      },
    ],
  },
  {
    id: "wardrobe",
    labelKey: "support.categories.wardrobe",
    descriptionKey: "support.categoryDescriptions.wardrobe",
    emailLabel: "Wardrobe Issues",
    backendCategory: "technical",
    icon: Shirt,
    color: "fuchsia",
    issues: [
      {
        id: "garment-upload",
        labelKey: "support.issues.garmentUpload",
        emailLabel: "Can't upload garment",
      },
      {
        id: "garment-mismatch",
        labelKey: "support.issues.garmentMismatch",
        emailLabel: "Garments do not match",
      },
      {
        id: "model-picture-mismatch",
        labelKey: "support.issues.modelPictureMismatch",
        emailLabel: "Model and my picture doesn't match",
      },
      {
        id: "try-on-not-working",
        labelKey: "support.issues.tryOnNotWorking",
        emailLabel: "\"Try on\" feature does not work",
      },
      {
        id: "archived-styles",
        labelKey: "support.issues.archivedStyles",
        emailLabel: "Can't see/download archived styles",
      },
    ],
  },
  {
    id: "privacy",
    labelKey: "support.categories.privacy",
    descriptionKey: "support.categoryDescriptions.privacy",
    emailLabel: "Data Privacy & Security",
    backendCategory: "account",
    icon: ShieldCheck,
    color: "emerald",
    issues: [
      {
        id: "personal-data",
        labelKey: "support.issues.personalData",
        emailLabel: "Inquiries regarding personal data",
        message: "required",
        attachments: false,
      },
      {
        id: "gdpr-kvkk",
        labelKey: "support.issues.gdprKvkk",
        emailLabel: "GDPR/KVKK inquiries",
        message: "required",
        attachments: false,
      },
      {
        id: "security-inquiry",
        labelKey: "support.issues.securityInquiry",
        emailLabel: "General security inquiries",
        message: "required",
        attachments: false,
      },
    ],
  },
  {
    id: "other",
    labelKey: "support.categories.other",
    descriptionKey: "support.categoryDescriptions.other",
    emailLabel: "Other",
    backendCategory: "general",
    icon: MessageCircleQuestion,
    color: "slate",
    issues: [
      {
        id: "other-issue",
        labelKey: "support.issues.otherIssue",
        emailLabel: "Other issues want to share",
        message: "required",
        attachments: false,
      },
    ],
  },
];

export const findCategory = (
  categoryId: string,
): SupportCategory | undefined =>
  supportCategories.find((category) => category.id === categoryId);

export const findIssue = (
  categoryId: string,
  issueId: string,
): SupportIssue | undefined =>
  findCategory(categoryId)?.issues.find((issue) => issue.id === issueId);

/** Backend sınırları — SendContactDto ile hizalı tutulmalı */
export const MAX_SUBJECT_LENGTH = 160;
export const MIN_MESSAGE_LENGTH = 10;

/**
 * Ticket'ın kısa tek satırlık özeti — subject alanı.
 * Destek ekibinin anlaması için sabit İngilizce etiket kullanılır.
 */
export const buildContactSubject = (
  category: SupportCategory,
  issue: SupportIssue,
): string =>
  `${category.emailLabel} — ${issue.emailLabel}`.slice(0, MAX_SUBJECT_LENGTH);

/**
 * Gönderilecek mesaj gövdesi. Mesajın opsiyonel olduğu maddelerde kullanıcı
 * bir şey yazmazsa madde etiketine düşer; backend 10 karakter alt sınırı
 * uyguladığı için kısa etiketler kategori adıyla zenginleştirilir.
 */
export const buildContactMessage = (
  category: SupportCategory,
  issue: SupportIssue,
  message: string,
): string => {
  const trimmed = message.trim();
  if (trimmed) return trimmed;

  const fallback = issue.emailLabel;
  return fallback.length >= MIN_MESSAGE_LENGTH
    ? fallback
    : `${category.emailLabel} — ${fallback}`;
};

/**
 * Backend'in category enum değerini döndürür.
 * category alanı `multipart/form-data`'da düz string olarak gönderilir.
 */
export const buildContactCategory = (
  category: SupportCategory,
): BackendCategory => category.backendCategory;

/** Talebin geldiği platform — backend'in `source` alanı */
export const CONTACT_SOURCE = "web";
